import { createSelector, Selector } from '@ngxs/store';

import type { FeatureAnnouncement, FeatureInteractions } from '../../models';
import { getActiveFeatureAnnouncements } from '../../utils/feature-announcement.utils';
import {
  FEATURE_ANNOUNCEMENT_LOAD_STATUS,
  FeatureAnnouncementLoadStatus,
  FeatureAnnouncementState,
  FeatureAnnouncementStateModel,
} from '../feature-announcement.state';

export class FeatureAnnouncementSelectors {
  @Selector([FeatureAnnouncementSelectors._features])
  static features(features: FeatureAnnouncement[]): FeatureAnnouncement[] {
    return features;
  }

  @Selector([FeatureAnnouncementSelectors._interactions])
  static interactions(interactions: FeatureInteractions): FeatureInteractions {
    return interactions;
  }

  @Selector([FeatureAnnouncementSelectors._status])
  static status(
    status: FeatureAnnouncementLoadStatus,
  ): FeatureAnnouncementLoadStatus {
    return status;
  }

  @Selector([FeatureAnnouncementSelectors._loaded])
  static loaded(loaded: boolean): boolean {
    return loaded;
  }

  @Selector([FeatureAnnouncementSelectors._activeFeatures])
  static activeFeatures(
    features: FeatureAnnouncement[],
  ): FeatureAnnouncement[] {
    return features;
  }

  @Selector([FeatureAnnouncementSelectors._unseenFeatures])
  static unseenFeatures(
    features: FeatureAnnouncement[],
  ): FeatureAnnouncement[] {
    return features;
  }

  @Selector([FeatureAnnouncementSelectors._unseenCount])
  static unseenCount(count: number): number {
    return count;
  }

  @Selector([FeatureAnnouncementSelectors._hasUnseen])
  static hasUnseen(hasUnseen: boolean): boolean {
    return hasUnseen;
  }

  static isSeen(featureKey: string) {
    return createSelector(
      [FeatureAnnouncementSelectors._interactions],
      (interactions: FeatureInteractions) =>
        Object.prototype.hasOwnProperty.call(interactions, featureKey),
    );
  }

  static isTourCompleted(featureKey: string) {
    return createSelector(
      [FeatureAnnouncementSelectors._interactions],
      (interactions: FeatureInteractions) =>
        Object.prototype.hasOwnProperty.call(interactions, featureKey)
          ? interactions[featureKey].tourCompleted
          : false,
    );
  }

  static featureByKey(featureKey: string) {
    return createSelector(
      [FeatureAnnouncementSelectors._features],
      (features: FeatureAnnouncement[]) =>
        features.find((feature) => feature.featureKey === featureKey),
    );
  }

  static unseenTargetFeature(target: string) {
    return createSelector(
      [FeatureAnnouncementSelectors._unseenFeatures],
      (features: FeatureAnnouncement[]) =>
        features.find(
          (feature) =>
            feature.navBadgeTarget === target ||
            feature.badgeTargets?.some(
              (badgeTarget) => badgeTarget.selector === target,
            ),
        ),
    );
  }

  static unseenNavTargetFeature(target: string) {
    return createSelector(
      [FeatureAnnouncementSelectors._unseenFeatures],
      (features: FeatureAnnouncement[]) =>
        features.find((feature) => feature.navBadgeTarget === target),
    );
  }

  @Selector([FeatureAnnouncementState])
  private static _features(
    state: FeatureAnnouncementStateModel,
  ): FeatureAnnouncement[] {
    return state.features;
  }

  @Selector([FeatureAnnouncementState])
  private static _interactions(
    state: FeatureAnnouncementStateModel,
  ): FeatureInteractions {
    return state.interactions;
  }

  @Selector([FeatureAnnouncementState])
  private static _status(
    state: FeatureAnnouncementStateModel,
  ): FeatureAnnouncementLoadStatus {
    return state.status;
  }

  @Selector([FeatureAnnouncementState])
  private static _loaded(state: FeatureAnnouncementStateModel): boolean {
    return state.status === FEATURE_ANNOUNCEMENT_LOAD_STATUS.READY;
  }

  @Selector([FeatureAnnouncementState])
  private static _activeFeatures(
    state: FeatureAnnouncementStateModel,
  ): FeatureAnnouncement[] {
    return getActiveFeatureAnnouncements(
      state.features,
      state.now,
      state.retentionMonths,
    );
  }

  @Selector([FeatureAnnouncementState])
  private static _unseenFeatures(
    state: FeatureAnnouncementStateModel,
  ): FeatureAnnouncement[] {
    return getActiveFeatureAnnouncements(
      state.features,
      state.now,
      state.retentionMonths,
    ).filter(
      (feature) =>
        !Object.prototype.hasOwnProperty.call(
          state.interactions,
          feature.featureKey,
        ),
    );
  }

  @Selector([FeatureAnnouncementSelectors._unseenFeatures])
  private static _unseenCount(features: FeatureAnnouncement[]): number {
    return features.length;
  }

  @Selector([FeatureAnnouncementSelectors._unseenCount])
  private static _hasUnseen(count: number): boolean {
    return count > 0;
  }
}
